import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import isElectron from 'is-electron';
import { checkElectronScreensharePermission } from 'flow/utils/electron-utils';
import { getTextHoverScreenShare } from 'flow/utils/meeting-utils';
import { screenShareIsSupported } from 'flow/utils/video-utils';
import { toggleSenderScreenShare } from 'flow/actions/meeting-actions';
import { selectVideoSenderScreenShareActive } from 'flow/selectors/video-selectors';
import { selectActiveMeetingScreenShareMemberName } from 'flow/selectors/meeting-selectors';
import { selectIsTeamsFullClient } from 'flow/selectors/selectors';
import useAppContext from 'flow/hooks/useAppContext';

/**
 * Hook that provides screen share state and controls.
 * Handles Electron permission checks, disabled state logic, and Electron screen share menu.
 */
export const useActiveMeetingScreenShare = (
    setElectronScreenShareMenuIsOpen: React.Dispatch<
        React.SetStateAction<boolean>
    >,
) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();

    const screenSharingIsActive = useAppSelector(
        selectVideoSenderScreenShareActive,
    );
    const otherAttendeeIsScreenSharing = !!useAppSelector(
        selectActiveMeetingScreenShareMemberName,
    );
    const isTeamsFullClient = useAppSelector(selectIsTeamsFullClient);

    const [hasElectronScreenShareAccess, setHasElectronScreenShareAccess] =
        useState(false);

    useEffect(() => {
        if (!isElectron()) return;

        const checkElectronScreenShareRights = async () => {
            const hasScreenShareRights =
                await checkElectronScreensharePermission();
            if (hasScreenShareRights) {
                setHasElectronScreenShareAccess(hasScreenShareRights);
            }
        };
        void checkElectronScreenShareRights();
    }, []);

    const togglingScreenSharingIsBlocked =
        (isElectron() && !hasElectronScreenShareAccess) ||
        otherAttendeeIsScreenSharing ||
        isTeamsFullClient;

    /**
     * Toggles screen sharing, handling Electron-specific behavior.
     * On Electron, opens the screen share source picker when starting.
     * On other platforms, directly toggles screen sharing.
     */
    const toggleScreenSharing = useCallback(() => {
        if (togglingScreenSharingIsBlocked) return;

        if (isElectron() && !screenSharingIsActive) {
            setElectronScreenShareMenuIsOpen(true);
        } else {
            void dispatch(toggleSenderScreenShare());
        }
    }, [
        togglingScreenSharingIsBlocked,
        screenSharingIsActive,
        setElectronScreenShareMenuIsOpen,
        dispatch,
    ]);

    const screenShareHoverText = useMemo(
        () =>
            getTextHoverScreenShare(
                hasElectronScreenShareAccess,
                otherAttendeeIsScreenSharing,
                screenSharingIsActive,
                isTeamsFullClient,
                i18next,
            ),
        [
            hasElectronScreenShareAccess,
            i18next,
            isTeamsFullClient,
            otherAttendeeIsScreenSharing,
            screenSharingIsActive,
        ],
    );

    return {
        screenShareIsSupported: screenShareIsSupported(),
        screenSharingIsActive,
        togglingScreenSharingIsBlocked,
        toggleScreenSharing,
        screenShareHoverText,
    };
};

export default useActiveMeetingScreenShare;
